import { formatPesoAmount } from '../utils/money';
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CgProfile } from "react-icons/cg";
import {
  FiBarChart2,
  FiBell,
  FiBox,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiHome,
  FiLogIn,
  FiMenu,
  FiMonitor,
  FiPercent,
  FiPrinter,
  FiSearch,
  FiShoppingBag,
  FiShoppingCart,
  FiStar,
  FiTag,
  FiTruck,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { RiSettings3Line, RiStore2Line } from "react-icons/ri";
import { tokenExists, handleLogout } from "../api/auth";
import InlineLoading from "./InlineLoading";
import { getBranches } from '../api';
import useCurrentUser from "../hooks/useCurrentUser";
import usePrincipalDispatch from "../hooks/usePrincipalDispatch";
import { hasAnyPermission, hasPermission } from "../utils/permissions";
import { buildVisibleNavSections } from "../config/navConfig";
import {
  buildSettingsPath,
  getVisibleSettingsTopics,
  SETTINGS_SECTION_SCOPES,
} from "../config/settingsNavigation";
import { normalizePaginatedResponse } from "../utils/pagination";
import {
  requestOpenPosNavbarCart,
  subscribePosNavbarCartState,
} from "../utils/posNavbarCart";
import {
  getSelectedBranchId,
  persistSelectedBranchId,
  subscribeSelectedBranch,
} from "../utils/selectedBranch";
import {
  getSelectedCompanyId,
  persistSelectedCompanyId,
  subscribeSelectedCompany,
} from "../utils/selectedCompany";
import {
  buildEquivalentBranchPath,
  getEquivalentBranchDestination,
} from "../utils/companyRoute";

const SECTION_ICONS = {
  home: FiHome,
  pos: FiShoppingBag,
  dispatches: FiTruck,
  sales: FiFileText,
  clients: FiUsers,
  catalog: FiTag,
  purchases: FiBox,
  projections: FiMonitor,
  reports: FiBarChart2,
};

const SETTINGS_TOPIC_ICONS = {
  products: FiBox,
  prices: FiPercent,
  "product-configuration": RiSettings3Line,
  "users-access": FiUsers,
  branches: RiStore2Line,
  "platform-administration": RiSettings3Line,
  "purchase-orders": FiFileText,
  notifications: FiBell,
  workspace: FiUser,
};

const SETTINGS_SECTION_ICONS = {
  "branch-directory": RiStore2Line,
  "branch-price-sync": RiSettings3Line,
  "branch-stock-requests": RiStore2Line,
  "purchase-order-reasons": FiFileText,
  "purchase-order-notifications": FiBell,
  notifications: FiBell,
  "purchase-order-review": RiSettings3Line,
  "purchase-order-visible-formats": FiTag,
  "purchase-order-default-categories": FiBox,
  "catalog-categories": FiBox,
  "catalog-subcategories": FiBox,
  "catalog-category-barcodes": FiTag,
  "catalog-price-list": FiBox,
  "catalog-product-formats": FiTag,
  "catalog-product-barcodes": RiSettings3Line,
  "catalog-product-search": FiSearch,
  "catalog-pos-sales": RiSettings3Line,
  "catalog-price-recommendations": RiSettings3Line,
  "price-auto-rule": RiSettings3Line,
  "catalog-products": FiBox,
  "client-pricing-categories": FiTag,
  "client-pricing-configuration": RiSettings3Line,
  "taxes-manage": FiPercent,
  "users-access": FiUsers,
  "registers-cash": FiHome,
  "registers-print-defaults": FiPrinter,
  "registers-barcode": RiSettings3Line,
  "registers-printing": FiPrinter,
  "registers-pos-favs": FiStar,
  companies: RiStore2Line,
};

const SidebarButton = ({
  icon: Icon,
  label,
  expanded,
  active = false,
  danger = false,
  onClick,
  children,
  ariaExpanded,
}) => (
  <button
    type="button"
    onClick={onClick}
    title={!expanded ? label : undefined}
    aria-label={label}
    aria-expanded={ariaExpanded}
    className={`relative flex h-11 w-full items-center rounded-md text-sm transition ${
      expanded ? "justify-start gap-3 px-3" : "justify-center px-0"
    } ${
      active
        ? "bg-blue-500/20 text-blue-100"
        : danger
          ? "text-red-300 hover:bg-red-500/10 hover:text-red-200"
          : "text-gray-200 hover:bg-gray-800 hover:text-white"
    }`}
  >
    <Icon size={20} className="flex-shrink-0" />
    {expanded && <span className="min-w-0 flex-1 truncate text-left">{label}</span>}
    {expanded && children}
  </button>
);

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, companies: sessionCompanies = [] } = useCurrentUser();
  const isClientPortal = location.pathname.startsWith("/client-portal");
  const match = location.pathname.match(/\/branches\/(\d+)/);
  const routeBranchId = match ? match[1] : "";
  const [selectedBranchId, setSelectedBranchId] = useState(getSelectedBranchId);
  const [selectedCompanyId, setSelectedCompanyId] = useState(getSelectedCompanyId);
  const branchId = routeBranchId || selectedBranchId;
  const { eligible: canDispatch } = usePrincipalDispatch(branchId, user);

  const [isPinnedOpen, setIsPinnedOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  const [isMediumScreen, setIsMediumScreen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 768px)").matches;
  });
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [branchMenuOpen, setBranchMenuOpen] = useState(false);
  const [branchSelectorOpen, setBranchSelectorOpen] = useState(false);
  const [companySelectorOpen, setCompanySelectorOpen] = useState(false);
  const [pendingBranchDestination, setPendingBranchDestination] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [branchesRequested, setBranchesRequested] = useState(false);
  const [branchError, setBranchError] = useState("");
  const [openSectionId, setOpenSectionId] = useState(null);
  const [openSettingsTopicIds, setOpenSettingsTopicIds] = useState(() => new Set());
  const [posCartState, setPosCartState] = useState({ visible: false, totalAmount: 0 });

  const isAuthenticated = tokenExists();
  const isExpanded = isMobileSidebarOpen || isPinnedOpen || (isLargeScreen && isHovering);
  const sidebarWidth = isExpanded ? "18rem" : isLargeScreen ? "4rem" : "3.5rem";
  const shouldShowDesktopSidebar = isMediumScreen;
  const shouldShowMobileNavbar = !isMediumScreen;
  const shouldRenderSidebar = shouldShowDesktopSidebar || isMobileSidebarOpen;

  const closeMenus = useCallback(() => {
    setOpenSectionId(null);
    setBranchMenuOpen(false);
    setBranchSelectorOpen(false);
    setCompanySelectorOpen(false);
    setProfileMenuOpen(false);
  }, []);

  const handleTogglePinned = () => {
    if (!isMediumScreen || isMobileSidebarOpen) {
      setIsMobileSidebarOpen(false);
      closeMenus();
      return;
    }
    setIsPinnedOpen((prev) => {
      const next = !prev;
      if (!next) {
        closeMenus();
      }
      return next;
    });
  };

  const ensureExpanded = () => {
    if (!isExpanded) {
      if (isMediumScreen) {
        setIsPinnedOpen(true);
      } else {
        setIsMobileSidebarOpen(true);
      }
    }
  };

  const toggleSection = (sectionId) => {
    ensureExpanded();
    setProfileMenuOpen(false);
    setBranchMenuOpen(false);
    setBranchSelectorOpen(false);
    setCompanySelectorOpen(false);
    setOpenSectionId((prev) => (prev === sectionId ? null : sectionId));
  };

  const toggleProfileMenu = () => {
    ensureExpanded();
    setOpenSectionId(null);
    setBranchMenuOpen(false);
    setBranchSelectorOpen(false);
    setProfileMenuOpen((prev) => !prev);
  };

  const toggleBranchMenu = () => {
    ensureExpanded();
    setOpenSectionId(null);
    setProfileMenuOpen(false);
    const nextBranchMenuOpen = !branchMenuOpen;
    setBranchMenuOpen(nextBranchMenuOpen);
    if (!nextBranchMenuOpen) {
      setBranchSelectorOpen(false);
      setCompanySelectorOpen(false);
    } else if (hasMultipleCompanies && !selectedCompanyId) {
      setCompanySelectorOpen(true);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mediumQuery = window.matchMedia("(min-width: 768px)");
    const largeQuery = window.matchMedia("(min-width: 1024px)");
    const update = () => {
      const nextIsMedium = mediumQuery.matches;
      const nextIsLarge = largeQuery.matches;
      setIsMediumScreen(nextIsMedium);
      setIsLargeScreen(nextIsLarge);
      if (nextIsMedium) {
        setIsMobileSidebarOpen(false);
      } else {
        setIsPinnedOpen(false);
      }
      if (!nextIsLarge) {
        setIsHovering(false);
      }
    };
    update();
    if (mediumQuery.addEventListener && largeQuery.addEventListener) {
      mediumQuery.addEventListener("change", update);
      largeQuery.addEventListener("change", update);
      return () => {
        mediumQuery.removeEventListener("change", update);
        largeQuery.removeEventListener("change", update);
      };
    }
    mediumQuery.addListener(update);
    largeQuery.addListener(update);
    return () => {
      mediumQuery.removeListener(update);
      largeQuery.removeListener(update);
    };
  }, []);

  const isPosPage = useMemo(
    () =>
      /^\/branches\/\d+\/(purchases\/new|purchases\/\d+\/edit|quotations\/new|quotations\/\d+\/edit)(\/|$)/.test(
        location.pathname
      ),
    [location.pathname]
  );
  const isPosClientView = useMemo(() => {
    if (!isPosPage) return false;
    const params = new URLSearchParams(location.search);
    return String(params.get("posView") || "").toLowerCase() === "client";
  }, [isPosPage, location.search]);

  useEffect(() => {
    const shouldApplySidebar = shouldShowDesktopSidebar && !isClientPortal && !isPosClientView;
    const shouldApplyMobileNavbar = shouldShowMobileNavbar && !isClientPortal && !isPosClientView;
    document.body.classList.toggle("has-app-sidebar", shouldApplySidebar);
    document.body.classList.toggle("has-mobile-navbar", shouldApplyMobileNavbar);
    return () => {
      document.body.classList.remove("has-app-sidebar");
      document.body.classList.remove("has-mobile-navbar");
    };
  }, [shouldShowDesktopSidebar, shouldShowMobileNavbar, isClientPortal, isPosClientView]);

  const navSections = useMemo(() => {
    if (!isAuthenticated) return [];
    return buildVisibleNavSections({ user, branchId, canDispatch });
  }, [user, branchId, canDispatch, isAuthenticated]);

  const visibleSettingsTopics = useMemo(
    () => (isAuthenticated ? getVisibleSettingsTopics(user) : []),
    [isAuthenticated, user]
  );

  useEffect(() => {
    const activeTopic = visibleSettingsTopics.find(
      (topic) => topic.collapsible && topic.sections.some(
        (section) => location.pathname === buildSettingsPath(topic.id, section.id)
      )
    );
    if (!activeTopic) return;
    setOpenSettingsTopicIds((current) => {
      if (current.has(activeTopic.id)) return current;
      const next = new Set(current);
      next.add(activeTopic.id);
      return next;
    });
  }, [location.pathname, visibleSettingsTopics]);

  const publicRoutes = [{ path: "/", label: "Login", icon: FiLogIn }, { path: "/display", label: "Pantallas de destacados", icon: FiStar }];
  const accessibleCompanies = useMemo(() => (
    (Array.isArray(sessionCompanies) ? sessionCompanies : [])
      .filter((company) => String(company?.status || 'active').toLowerCase() === 'active')
      .filter((company) => !company?.deletedAt && company?.isDeleted !== true)
  ), [sessionCompanies]);
  const currentCompany = useMemo(
    () => accessibleCompanies.find((company) => String(company.id) === String(selectedCompanyId)),
    [accessibleCompanies, selectedCompanyId]
  );
  const currentCompanyLabel = currentCompany?.name || currentCompany?.companyName
    || (selectedCompanyId ? `Empresa ${selectedCompanyId}` : 'Sin empresa seleccionada');
  const hasMultipleCompanies = accessibleCompanies.length > 1;
  const canReadBranches = hasPermission(user, "branch.read");
  const canAccessSettings = visibleSettingsTopics.length > 0;
  const shouldShowPosCart = isAuthenticated && isPosPage && !isPosClientView && posCartState.visible;
  const formattedPosCartTotal = formatPesoAmount(Number(posCartState.totalAmount || 0));
  const userDisplayName = String(user?.name || "").trim();
  const userDisplayEmail = String(user?.email || user?.username || "").trim();
  const currentBranchId = branchId ? String(branchId) : "";
  const currentBranch = useMemo(
    () => branches.find((branch) => String(branch.id) === currentBranchId),
    [branches, currentBranchId]
  );
  const currentBranchLabel = currentBranch?.branchName
    || (currentBranchId ? `Sucursal ${currentBranchId}` : "Sin sucursal seleccionada");
  const currentContextLabel = hasMultipleCompanies || !selectedCompanyId
    ? `${currentCompanyLabel} · ${currentBranchLabel}`
    : currentBranchLabel;
  const hasSingleBranch = branches.length === 1;

  const fetchBranches = useCallback(async () => {
    if (!tokenExists() || !canReadBranches || !selectedCompanyId) return;
    setBranchesRequested(true);
    setBranchLoading(true);
    setBranchError("");
    try {
      const data = await getBranches({ pageSize: 0 });
      const normalized = normalizePaginatedResponse(data);
      setBranches(normalized.data || []);
    } catch (err) {
      console.error("Error fetching branches:", err);
      setBranchError("No se pudieron cargar las sucursales.");
    } finally {
      setBranchLoading(false);
    }
  }, [canReadBranches, selectedCompanyId]);

  useEffect(() => {
    setOpenSectionId(location.pathname === "/settings" || location.pathname.startsWith("/settings/")
      ? "settings"
      : null);
    setBranchMenuOpen(Boolean(pendingBranchDestination));
    setBranchSelectorOpen(Boolean(pendingBranchDestination));
    setProfileMenuOpen(false);
    setIsMobileSidebarOpen(false);
  }, [location.pathname, pendingBranchDestination]);

  useEffect(() => {
    if (!isAuthenticated || !canReadBranches || !selectedCompanyId) return;
    if (branchesRequested || branches.length || branchLoading) return;
    fetchBranches();
  }, [isAuthenticated, canReadBranches, selectedCompanyId, branchesRequested, branches.length, branchLoading, fetchBranches]);

  useEffect(() => subscribeSelectedBranch(setSelectedBranchId), []);

  useEffect(() => subscribeSelectedCompany((companyId) => {
    setSelectedCompanyId(companyId);
    setBranches([]);
    setBranchesRequested(false);
    setBranchError('');
    setBranchSelectorOpen(Boolean(companyId));
  }), []);

  useEffect(() => {
    if (!branchesRequested || branchLoading || branchError || (routeBranchId && !pendingBranchDestination)) return;
    const allowedBranchIds = new Set(branches.map((branch) => String(branch.id)));
    if (selectedBranchId && allowedBranchIds.has(String(selectedBranchId))) return;
    if (branches.length === 1) {
      persistSelectedBranchId(branches[0].id);
      if (pendingBranchDestination) {
        const canOpenEquivalent = !pendingBranchDestination.permission
          || hasPermission(user, pendingBranchDestination.permission);
        const nextPath = canOpenEquivalent
          ? buildEquivalentBranchPath(branches[0].id, pendingBranchDestination)
          : '/home';
        setPendingBranchDestination(null);
        navigate(`${nextPath}${canOpenEquivalent ? pendingBranchDestination.search || '' : ''}`, { replace: true });
      }
      return;
    }
    if (selectedBranchId) persistSelectedBranchId('');
  }, [
    branchError,
    branchLoading,
    branches,
    branchesRequested,
    routeBranchId,
    selectedBranchId,
    pendingBranchDestination,
    navigate,
    user,
  ]);

  useEffect(() => {
    const unsubscribe = subscribePosNavbarCartState((event) => {
      const detail = event?.detail || {};
      const nextTotal = Number(detail.totalAmount);
      setPosCartState({
        visible: Boolean(detail.visible),
        totalAmount: Number.isFinite(nextTotal) ? nextTotal : 0,
      });
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!isPosPage) {
      setPosCartState({ visible: false, totalAmount: 0 });
    }
  }, [isPosPage]);

  const routeIsActive = (path) => {
    if (!path) return false;
    const pathname = path.split('?')[0];
    return location.pathname === pathname || (pathname !== "/" && location.pathname.startsWith(`${pathname}/`));
  };

  const sectionIsActive = (section) => section.items.some((item) => routeIsActive(item.path));

  const handleSelectCompany = (nextCompanyId) => {
    const nextId = String(nextCompanyId || '');
    if (!nextId || nextId === String(selectedCompanyId)) {
      setCompanySelectorOpen(false);
      return;
    }

    const equivalent = getEquivalentBranchDestination(location.pathname);
    const pending = equivalent ? { ...equivalent, search: location.search } : null;
    setPendingBranchDestination(pending);
    persistSelectedCompanyId(nextId, { clearBranch: true });
    setCompanySelectorOpen(false);
    setBranchSelectorOpen(true);
    setBranchMenuOpen(true);
    setBranches([]);
    setBranchesRequested(false);

    if (match) {
      navigate('/home', { replace: true });
    }
  };

  const handleSelectBranch = (nextBranchId) => {
    const nextId = String(nextBranchId);
    if (!nextId) return;
    persistSelectedBranchId(nextId);
    setBranchSelectorOpen(false);
    setBranchMenuOpen(false);
    setIsMobileSidebarOpen(false);
    if (pendingBranchDestination) {
      const canOpenEquivalent = !pendingBranchDestination.permission
        || hasPermission(user, pendingBranchDestination.permission);
      const nextPath = canOpenEquivalent
        ? buildEquivalentBranchPath(nextId, pendingBranchDestination)
        : '/home';
      const nextSearch = canOpenEquivalent ? pendingBranchDestination.search || '' : '';
      setPendingBranchDestination(null);
      navigate(`${nextPath}${nextSearch}`, { replace: true });
      return;
    }
    if (match) {
      const nextPath = location.pathname.replace(/\/branches\/\d+/, `/branches/${nextId}`);
      navigate(`${nextPath}${location.search}`, { replace: true });
    }
  };

  const handleNavigate = (path) => {
    if (!path) return;
    closeMenus();
    setIsMobileSidebarOpen(false);
    if (location.pathname === path) {
      window.location.href = path;
      return;
    }
    navigate(path);
  };

  const toggleSettingsMenu = () => {
    ensureExpanded();
    setBranchMenuOpen(false);
    setBranchSelectorOpen(false);
    setProfileMenuOpen(false);
    setOpenSectionId((prev) => (prev === "settings" ? null : "settings"));
  };

  const handleRequestBranchSelection = () => {
    ensureExpanded();
    setOpenSectionId(null);
    setProfileMenuOpen(false);
    setBranchMenuOpen(true);
    setCompanySelectorOpen(hasMultipleCompanies && !selectedCompanyId);
    setBranchSelectorOpen(Boolean(selectedCompanyId));
    if (selectedCompanyId && !branchesRequested && !branchLoading) fetchBranches();
  };

  const toggleSettingsTopic = (topicId) => {
    setOpenSettingsTopicIds((current) => {
      const next = new Set(current);
      if (next.has(topicId)) {
        next.delete(topicId);
      } else {
        next.add(topicId);
      }
      return next;
    });
  };

  const handleOpenPosCart = () => {
    closeMenus();
    setIsMobileSidebarOpen(false);
    requestOpenPosNavbarCart();
  };

  const handleLogoClick = () => {
    const canEnterPos = hasAnyPermission(user, ["pos.sell", "pos.open", "pos.close"]);
    const targetPath = canEnterPos && branchId
      ? `/branches/${branchId}/purchases/new`
      : isAuthenticated ? "/home" : "/";
    if (location.pathname === targetPath) {
      window.location.reload();
      return;
    }
    navigate(targetPath);
  };

  const handleLogoutClick = () => {
    closeMenus();
    setIsMobileSidebarOpen(false);
    handleLogout();
  };

  const renderBranchPanel = () => (
    <div className="mt-2 rounded-lg border border-gray-800 bg-gray-950/60">
      <div className="px-3 py-3 border-b border-gray-800">
        <div className="text-xs uppercase text-gray-400">Contexto actual</div>
        <div className="text-sm font-medium text-gray-100">{currentContextLabel}</div>
        {accessibleCompanies.length === 0 && (
          <div className="mt-2 text-xs text-amber-300">No tienes empresas activas disponibles.</div>
        )}
        {!canReadBranches && (
          <div className="text-xs text-gray-400 mt-2">
            No tienes permisos para ver sucursales.
          </div>
        )}
        {branchError && (
          <div className="text-xs text-red-300 mt-2">{branchError}</div>
        )}
        {branchLoading && (
          <div className="text-xs text-gray-400 mt-2">
            <InlineLoading label="Cargando sucursales" />
          </div>
        )}
        {!branchLoading && !branchError && canReadBranches && selectedCompanyId && branches.length === 0 && (
          <div className="text-xs text-gray-400 mt-2">No hay sucursales disponibles.</div>
        )}
      </div>
      <div className="px-3 py-3">
        {hasMultipleCompanies && (
          <div className="mb-3 border-b border-gray-800 pb-3">
            <button
              type="button"
              onClick={() => setCompanySelectorOpen((current) => !current)}
              aria-expanded={companySelectorOpen}
              aria-controls="allowed-company-selector"
              className="flex w-full items-center gap-2 rounded bg-gray-800 px-3 py-2 text-left text-gray-100 transition hover:bg-gray-700"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-xs text-gray-400">Empresa</span>
                <span className="block truncate text-sm">{currentCompanyLabel}</span>
              </span>
              <FiChevronDown
                size={16}
                className={`flex-shrink-0 transition-transform ${companySelectorOpen ? "rotate-180" : ""}`}
              />
            </button>
            {companySelectorOpen && (
              <div
                id="allowed-company-selector"
                className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-md border border-gray-800 bg-gray-950 p-1"
              >
                {accessibleCompanies.map((company) => {
                  const isSelected = String(company.id) === String(selectedCompanyId);
                  return (
                    <button
                      key={company.id}
                      type="button"
                      onClick={() => handleSelectCompany(company.id)}
                      disabled={isSelected}
                      aria-current={isSelected ? "true" : undefined}
                      className={`w-full rounded px-3 py-2 text-left transition ${
                        isSelected
                          ? "bg-blue-500/20 text-blue-100"
                          : "text-gray-200 hover:bg-gray-800 hover:text-white"
                      }`}
                    >
                      <div className="truncate text-sm font-medium">{company.name || company.companyName}</div>
                      <div className={`text-xs ${isSelected ? "text-blue-200" : "text-gray-500"}`}>
                        Empresa {company.id}{isSelected ? " · Actual" : ""}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={() => setBranchSelectorOpen((current) => !current)}
          disabled={!selectedCompanyId || !canReadBranches || branchLoading || Boolean(branchError) || branches.length === 0}
          aria-expanded={branchSelectorOpen}
          aria-controls="allowed-branch-selector"
          className="flex w-full items-center gap-2 rounded bg-gray-800 px-3 py-2 text-left text-gray-100 transition hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="min-w-0 flex-1">
            {!selectedCompanyId
              ? "Selecciona una empresa primero"
              : currentBranchId ? "Cambiar sucursal" : "Seleccionar sucursal"}
          </span>
          <FiChevronDown
            size={16}
            className={`flex-shrink-0 transition-transform ${branchSelectorOpen ? "rotate-180" : ""}`}
          />
        </button>
        {branchSelectorOpen && (
          <div
            id="allowed-branch-selector"
            className="mt-2 max-h-56 space-y-1 overflow-y-auto rounded-md border border-gray-800 bg-gray-950 p-1"
          >
            {branches.map((branch) => {
              const isSelected = String(branch.id) === currentBranchId;
              return (
                <button
                  key={branch.id}
                  type="button"
                  onClick={() => handleSelectBranch(branch.id)}
                  disabled={isSelected}
                  aria-current={isSelected ? "true" : undefined}
                  className={`w-full rounded px-3 py-2 text-left transition ${
                    isSelected
                      ? "bg-blue-500/20 text-blue-100"
                      : "text-gray-200 hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  <div className="truncate text-sm font-medium">{branch.branchName}</div>
                  <div className={`text-xs ${isSelected ? "text-blue-200" : "text-gray-500"}`}>
                    Sucursal {branch.id}{isSelected ? " · Actual" : ""}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {hasSingleBranch && currentBranchId && !branchSelectorOpen && (
          <div className="mt-2 text-xs text-gray-400">
            Solo tienes acceso a una sucursal.
          </div>
        )}
      </div>
    </div>
  );

  const renderProfilePanel = () => (
    <div className="mt-2 rounded-lg border border-gray-800 bg-gray-950/60">
      {(userDisplayName || userDisplayEmail) && (
        <div className="px-3 py-3 border-b border-gray-800">
          <p className="text-xs uppercase text-gray-400">Sesión activa</p>
          <p className="text-sm font-medium text-gray-100 truncate">
            {userDisplayName || userDisplayEmail}
          </p>
          {userDisplayName && userDisplayEmail && userDisplayEmail.toLowerCase() !== userDisplayName.toLowerCase() && (
            <p className="text-xs text-gray-400 truncate">{userDisplayEmail}</p>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={() => handleNavigate("/change-password")}
        className="w-full px-3 py-2 text-left text-sm text-gray-100 hover:bg-gray-800 hover:text-white"
      >
        Mi Perfil
      </button>
      <button
        type="button"
        onClick={handleLogoutClick}
        className="w-full px-3 py-2 text-left text-sm text-red-300 hover:bg-red-500/10 hover:text-red-200"
      >
        Cerrar Sesión
      </button>
    </div>
  );

  const renderSettingsPanel = () => (
    <div className="mt-2 max-h-[55vh] overflow-y-auto rounded-lg border border-gray-800 bg-gray-950/60 py-2">
      {visibleSettingsTopics.map((topic) => {
        const TopicIcon = SETTINGS_TOPIC_ICONS[topic.id] || RiSettings3Line;
        const topicActive = topic.sections.some((section) =>
          location.pathname === buildSettingsPath(topic.id, section.id)
        );
        const topicOpen = !topic.collapsible || openSettingsTopicIds.has(topic.id);
        const topicPanelId = `settings-topic-${topic.id}`;
        return (
          <div key={topic.id} className="px-2 py-1">
            {topic.collapsible ? (
              <button
                type="button"
                onClick={() => toggleSettingsTopic(topic.id)}
                aria-expanded={topicOpen}
                aria-controls={topicPanelId}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-xs font-semibold uppercase tracking-wide transition hover:bg-gray-800 hover:text-white ${
                  topicActive ? "bg-blue-500/10 text-blue-100" : "text-gray-400"
                }`}
              >
                <TopicIcon size={14} className="flex-shrink-0" />
                <span className="min-w-0 flex-1 truncate text-left">{topic.label}</span>
                <FiChevronDown
                  size={14}
                  className={`flex-shrink-0 transition-transform ${topicOpen ? "rotate-180" : ""}`}
                />
              </button>
            ) : (
              <div
                className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-semibold uppercase tracking-wide ${
                  topicActive ? "text-blue-100" : "text-gray-400"
                }`}
              >
                <TopicIcon size={14} className="flex-shrink-0" />
                <span className="min-w-0 truncate">{topic.label}</span>
              </div>
            )}
            {topicOpen && <div id={topicPanelId} className="mt-1 space-y-1 pl-4">
              {topic.sections.map((section, sectionIndex) => {
                const path = buildSettingsPath(topic.id, section.id);
                const itemActive = location.pathname === path;
                const SectionIcon = SETTINGS_SECTION_ICONS[section.id] || RiSettings3Line;
                const sectionScope = SETTINGS_SECTION_SCOPES[section.id];
                const showGroupLabel =
                  section.group && section.group !== topic.sections[sectionIndex - 1]?.group;
                return (
                  <React.Fragment key={section.id}>
                    {showGroupLabel && (
                      <div className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        {section.group}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => handleNavigate(path)}
                      className={`w-full rounded-md px-3 py-2 text-left text-sm transition ${
                        itemActive
                          ? "bg-blue-500/20 text-blue-100"
                          : "text-gray-300 hover:bg-gray-800 hover:text-white"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <SectionIcon size={14} className="mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{section.label}</div>
                          <div className="mt-0.5 line-clamp-2 text-xs text-gray-500">
                            {sectionScope?.label || section.description}
                          </div>
                        </div>
                      </div>
                    </button>
                  </React.Fragment>
                );
              })}
            </div>}
          </div>
        );
      })}
    </div>
  );

  const renderSection = (section) => {
    const Icon = SECTION_ICONS[section.id] || FiMenu;
    const active = sectionIsActive(section);
    const sectionOpen = openSectionId === section.id;

    if (section.isSingle) {
      const item = section.items[0];
      return (
        <SidebarButton
          key={section.id}
          icon={Icon}
          label={section.label}
          expanded={isExpanded}
          active={active}
          onClick={() => (
            item.requiresBranchSelection
              ? handleRequestBranchSelection()
              : handleNavigate(section.directPath)
          )}
        />
      );
    }

    return (
      <div key={section.id}>
        <SidebarButton
          icon={Icon}
          label={section.label}
          expanded={isExpanded}
          active={active}
          onClick={() => toggleSection(section.id)}
          ariaExpanded={sectionOpen}
        >
          <FiChevronDown
            size={16}
            className={`flex-shrink-0 transition-transform ${sectionOpen ? "rotate-180" : ""}`}
          />
        </SidebarButton>
        {isExpanded && sectionOpen && (
          <div className="mt-1 space-y-1 pl-9">
            {section.items.map((item) => {
              const itemActive = routeIsActive(item.path);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => (
                    item.requiresBranchSelection
                      ? handleRequestBranchSelection()
                      : handleNavigate(item.path)
                  )}
                  className={`w-full rounded-md px-3 py-2 text-left text-sm transition ${
                    itemActive
                      ? "bg-blue-500/20 text-blue-100"
                      : "text-gray-300 hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (isClientPortal || isPosClientView) return null;

  return (
    <>
      {shouldShowMobileNavbar && (
        <nav className="fixed left-0 top-0 z-[940] flex h-14 w-full items-center justify-between border-b border-gray-800 bg-gray-900 px-3 text-white shadow-md">
          <button
            type="button"
            onClick={() => setIsMobileSidebarOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-300"
            aria-label="Abrir menú"
            aria-expanded={isMobileSidebarOpen}
          >
            <FiMenu size={22} />
          </button>
          <img
            src="/logo.png"
            alt="Logo"
            className="h-9 cursor-pointer"
            onClick={handleLogoClick}
          />
          {shouldShowPosCart ? (
            <button
              type="button"
              onClick={handleOpenPosCart}
              className="relative inline-flex h-10 w-10 items-center justify-center rounded-md text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-300"
              aria-label={`Abrir carrito, total $${formattedPosCartTotal}`}
              title={`Carrito: $${formattedPosCartTotal}`}
            >
              <FiShoppingCart size={22} />
              <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-blue-500 ring-2 ring-gray-900" />
            </button>
          ) : (
            <div className="h-10 w-10" aria-hidden="true" />
          )}
        </nav>
      )}

      {isMobileSidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-[1390] bg-black/50 md:hidden"
          onClick={() => {
            setIsMobileSidebarOpen(false);
            closeMenus();
          }}
          aria-label="Cerrar menú"
        />
      )}

      {shouldRenderSidebar && (
        <aside
          style={{ width: sidebarWidth, minWidth: sidebarWidth, maxWidth: sidebarWidth }}
          className={`fixed inset-y-0 left-0 flex flex-col overflow-hidden border-r border-gray-800 bg-gray-900 text-gray-100 shadow-2xl transition-[width] duration-200 ease-out ${
            isMobileSidebarOpen ? "z-[1400]" : "z-[1300]"
          }`}
          onMouseEnter={() => {
            if (shouldShowDesktopSidebar && isLargeScreen) setIsHovering(true);
          }}
          onMouseLeave={() => {
            if (shouldShowDesktopSidebar && isLargeScreen) setIsHovering(false);
          }}
        >
        <div className="flex-shrink-0 border-b border-gray-800">
          <div className={`flex h-14 items-center ${isExpanded ? "justify-between px-3" : "justify-center"}`}>
            {isExpanded && (
              <img
                src="/logo.png"
                alt="Logo"
                className="h-9 cursor-pointer"
                onClick={handleLogoClick}
              />
            )}
            <button
              type="button"
              onClick={handleTogglePinned}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-200 hover:bg-gray-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-300"
              aria-label={isPinnedOpen ? "Reducir menú" : "Expandir menú"}
              title={isPinnedOpen ? "Reducir menú" : "Expandir menú"}
            >
              {isExpanded ? <FiChevronLeft size={20} /> : <FiChevronRight size={20} />}
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-4">
          {shouldShowPosCart && (
            <div className="mb-3">
              <SidebarButton
                icon={FiShoppingCart}
                label="Carrito"
                expanded={isExpanded}
                active={false}
                onClick={handleOpenPosCart}
              >
                <span className="text-xs font-semibold text-blue-200">
                  ${formattedPosCartTotal}
                </span>
              </SidebarButton>
            </div>
          )}

          <nav aria-label="Navegación principal" className="space-y-1">
            {isAuthenticated
              ? navSections.map(renderSection)
              : publicRoutes.map((route) => (
                <SidebarButton
                  key={route.path}
                  icon={route.icon}
                  label={route.label}
                  expanded={isExpanded}
                  active={routeIsActive(route.path)}
                  onClick={() => handleNavigate(route.path)}
                />
              ))}
          </nav>
        </div>

        {isAuthenticated && (
          <div className="flex-shrink-0 space-y-1 border-t border-gray-800 px-2 py-4">
            <SidebarButton
              icon={RiStore2Line}
              label={currentContextLabel}
              expanded={isExpanded}
              active={branchMenuOpen}
              onClick={toggleBranchMenu}
              ariaExpanded={branchMenuOpen}
            >
              <FiChevronDown
                size={16}
                className={`flex-shrink-0 transition-transform ${branchMenuOpen ? "rotate-180" : ""}`}
              />
            </SidebarButton>
            {isExpanded && branchMenuOpen && renderBranchPanel()}

            {canAccessSettings && (
              <>
                <SidebarButton
                  icon={RiSettings3Line}
                  label="Configuración"
                  expanded={isExpanded}
                  active={routeIsActive("/settings")}
                  onClick={toggleSettingsMenu}
                  ariaExpanded={openSectionId === "settings"}
                >
                  <FiChevronDown
                    size={16}
                    className={`flex-shrink-0 transition-transform ${
                      openSectionId === "settings" ? "rotate-180" : ""
                    }`}
                  />
                </SidebarButton>
                {isExpanded && openSectionId === "settings" && renderSettingsPanel()}
              </>
            )}

            <SidebarButton
              icon={CgProfile}
              label={userDisplayName || userDisplayEmail || "Mi Perfil"}
              expanded={isExpanded}
              active={profileMenuOpen}
              onClick={toggleProfileMenu}
              ariaExpanded={profileMenuOpen}
            >
              <FiChevronDown
                size={16}
                className={`flex-shrink-0 transition-transform ${profileMenuOpen ? "rotate-180" : ""}`}
              />
            </SidebarButton>
            {isExpanded && profileMenuOpen && renderProfilePanel()}
          </div>
        )}
        </aside>
      )}

    </>
  );
};

export default Navbar;
